import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  VISIOUS_SCOPES,
  type CreatedPlaylist,
  type CreatePlaylistInput,
  type PlaylistEntry,
  type UpdatePlaylistInput,
} from '@visious/core';
import type { AuthApiProblem } from '../api/auth';
import {
  addPlaylistVideo,
  createPlaylist,
  deletePlaylist,
  removePlaylistVideo,
  updatePlaylist,
} from '../api/playlists';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';
import {
  authPlaylistKey,
  invalidatePlaylist,
  markOwnedPlaylistsStale,
  noteEntryAdded,
  ownedPlaylistsKey,
} from './useOwnedPlaylists';
import { noteWatchLaterEntryAdded, noteWatchLaterEntryRemoved } from './watchLaterCache';

/**
 * The five mutations, as hooks — and one deliberate departure from how every other request in this app
 * is made.
 *
 * **These are not `useMutation`.** Not from principle: TanStack's mutation object is built around a
 * *throwing* `mutationFn`, and this codebase's whole transport returns failures as values (1c). Wrapping
 * each one in a throw so a library can catch it and hand back an `unknown` error, which then has to be
 * unwrapped to get the `AuthApiProblem` out again, is three conversions to arrive where the call already
 * was. What `useMutation` would add on top of that is retry (which core owns), caching (a mutation has
 * nothing to cache) and `isPending` (one boolean).
 *
 * So each hook is: a function that returns a `Result`, plus the busy flag, plus the invalidation. The
 * invalidation is the part worth centralising, and it is not uniform — see `useOwnedPlaylists.ts` for
 * why an add updates the cache in place while a removal only marks it stale.
 *
 * **The grant is passed, unlike on a read.** `api/auth.ts` says why in full: a mutation the token cannot
 * make must not reach the wire, because a `403` on a write leaves "did it happen?" unanswerable.
 */

/** What every one of these returns: the value, or a problem the caller can turn into a sentence. */
export type WriteResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: AuthApiProblem };

const BAD_PLATFORM: AuthApiProblem = { kind: 'bad-url', path: '/api/v1/auth/playlists' };

function useGrantKey(): number {
  const session = useSession();
  return session.grantedAtEpochSec ?? 0;
}

/**
 * One in-flight write at a time per hook, and the flag is a `Set` of keys rather than a boolean so a
 * list of rows can each show their own state — removing entry 4 must not grey out entry 5.
 */
function useBusyKeys(): {
  readonly busy: ReadonlySet<string>;
  readonly run: <T>(key: string, work: () => Promise<T>) => Promise<T>;
} {
  const [busy, setBusy] = useState<ReadonlySet<string>>(EMPTY);

  const run = useCallback(async <T,>(key: string, work: () => Promise<T>): Promise<T> => {
    setBusy((previous) => new Set(previous).add(key));
    try {
      return await work();
    } finally {
      setBusy((previous) => {
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
    }
  }, []);

  return { busy, run };
}

const EMPTY: ReadonlySet<string> = new Set<string>();

/* ------------------------------------------------------------------------------------------------
 * Create
 * ---------------------------------------------------------------------------------------------- */

export interface CreatePlaylistMutation {
  readonly create: (input: CreatePlaylistInput) => Promise<WriteResult<CreatedPlaylist>>;
  readonly creating: boolean;
}

export function useCreatePlaylist(): CreatePlaylistMutation {
  const client = useQueryClient();
  const grantKey = useGrantKey();
  const { busy, run } = useBusyKeys();

  const create = useCallback(
    async (input: CreatePlaylistInput): Promise<WriteResult<CreatedPlaylist>> =>
      run('create', async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await createPlaylist(boot.platform, input, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        // The list is the page this happens on, so an ordinary invalidation is right here: it is
        // mounted, the new row belongs in it, and the response carries only an id and a title.
        void client.invalidateQueries({ queryKey: ownedPlaylistsKey(grantKey) });
        return { ok: true as const, value: result.value };
      }),
    [client, grantKey, run],
  );

  return { create, creating: busy.has('create') };
}

/* ------------------------------------------------------------------------------------------------
 * Update, delete
 * ---------------------------------------------------------------------------------------------- */

export interface EditPlaylistMutations {
  readonly update: (playlistId: string, input: UpdatePlaylistInput) => Promise<WriteResult<null>>;
  readonly remove: (playlistId: string) => Promise<WriteResult<null>>;
  readonly saving: boolean;
  readonly deleting: boolean;
}

export function useEditPlaylist(): EditPlaylistMutations {
  const client = useQueryClient();
  const grantKey = useGrantKey();
  const { busy, run } = useBusyKeys();

  const update = useCallback(
    async (playlistId: string, input: UpdatePlaylistInput): Promise<WriteResult<null>> =>
      run('update', async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await updatePlaylist(boot.platform, playlistId, input, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        invalidatePlaylist(client, grantKey, playlistId);
        // A title, description or privacy change is visible in the list too, and the list is cheap to
        // *mark* stale. It is not refetched now: the reader is on the playlist, not the list.
        markOwnedPlaylistsStale(client, grantKey);
        return { ok: true as const, value: null };
      }),
    [client, grantKey, run],
  );

  const remove = useCallback(
    async (playlistId: string): Promise<WriteResult<null>> =>
      run('delete', async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await deletePlaylist(boot.platform, playlistId, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        // Removed rather than invalidated: the playlist is gone, so refetching it would fetch a 404 and
        // cache *that*. The caller navigates away.
        client.removeQueries({ queryKey: authPlaylistKey(playlistId, grantKey) });
        client.removeQueries({ queryKey: ['playlist', playlistId] });
        void client.invalidateQueries({ queryKey: ownedPlaylistsKey(grantKey) });
        return { ok: true as const, value: null };
      }),
    [client, grantKey, run],
  );

  return { update, remove, saving: busy.has('update'), deleting: busy.has('delete') };
}

/* ------------------------------------------------------------------------------------------------
 * Add and remove an entry
 * ---------------------------------------------------------------------------------------------- */

/**
 * These two are the **single crossing point** for every control that changes what is in a playlist:
 * the Save-to-playlist dialog, the playlist page's per-row remove, the watch page's playlist panel,
 * and the Watch later clock. So all three caches that hold an opinion about a playlist's contents
 * are updated here rather than by whichever caller happened to fire (2026-08-03) — the owned
 * list and the playlist's own read, which were already here, and the Watch later membership list,
 * which used to be patched by `useWatchLater` alone and was therefore stale after any of the other
 * three: a video removed from the appointed list on the playlist page left every clock button on
 * screen still saying "saved".
 */
export interface EntryMutations {
  readonly add: (playlistId: string, videoId: string) => Promise<WriteResult<PlaylistEntry>>;
  readonly remove: (playlistId: string, indexId: string) => Promise<WriteResult<null>>;
  /** Keyed by `playlistId` for an add and by `indexId` for a removal, so rows are independent. */
  readonly busy: ReadonlySet<string>;
}

export function useEntryMutations(): EntryMutations {
  const client = useQueryClient();
  const grantKey = useGrantKey();
  const { busy, run } = useBusyKeys();

  const add = useCallback(
    async (playlistId: string, videoId: string): Promise<WriteResult<PlaylistEntry>> =>
      run(playlistId, async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await addPlaylistVideo(boot.platform, playlistId, videoId, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        // In place, not invalidated — the menu is open, so the list query is mounted and an invalidation
        // would refetch the whole thing (`noteEntryAdded`).
        noteEntryAdded(client, grantKey, playlistId, result.value);
        // A no-op unless this playlist is the appointed one, and free to call either way.
        noteWatchLaterEntryAdded(client, grantKey, playlistId, result.value);
        invalidatePlaylist(client, grantKey, playlistId);
        return { ok: true as const, value: result.value };
      }),
    [client, grantKey, run],
  );

  const remove = useCallback(
    async (playlistId: string, indexId: string): Promise<WriteResult<null>> =>
      run(indexId, async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await removePlaylistVideo(boot.platform, playlistId, indexId, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        noteWatchLaterEntryRemoved(client, grantKey, playlistId, indexId);
        invalidatePlaylist(client, grantKey, playlistId);
        markOwnedPlaylistsStale(client, grantKey);
        return { ok: true as const, value: null };
      }),
    [client, grantKey, run],
  );

  return { add, remove, busy };
}
